/**
 * mutualFundPickerStyle — the colour / shape hints of the fund picker card (`type: 'MF'`,
 * refId `type14`, configs FUND_PICKER / FUND_PICKER_COMPARE) as inline styles for
 * `MutualFundCardPreview`. Kept out of the SFC so it runs under plain Node
 * (see tests/mutualFundPickerStyle.test.mjs).
 *
 * 🔴 THE HINTS SIT ON THE CARD, NOT UNDER `data`. type14 is a flat envelope, so every function
 *    here takes the card object itself — reading `card.data` would find nothing and the preview
 *    would keep drawing the stylesheet's colours while the phone draws the config's (#925).
 *
 * 🔴 NOTHING AUTHORED ⇒ null. A `:style` binding then adds no attribute and the stylesheet's
 *    fallback pill stands, so a config without these keys renders exactly as before.
 *
 * Units: dp / sp → px 1:1, the same approximation `textStyle` makes. Numbers only — a string
 * where the app expects a number is an authoring mistake the phone drops, so it is ignored here too.
 */
import { hintColor, textStyle, mergeStyles } from './taxCardFormat.js';

/** One dp hint (`src[key]`, a finite number ≥ 0) as `{ [prop]: '<n>px' }`, or null. */
function dpStyle(src, key, prop) {
  const v = src && typeof src === 'object' ? src[key] : undefined;
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? { [prop]: v + 'px' } : null;
}

/**
 * `<prefix>BorderColor` + `<prefix>BorderWidth` as one CSS border, or null.
 * Width 0 is a real value ("no border") and removes the stylesheet's border too.
 */
function borderStyle(src, prefix) {
  if (!src || typeof src !== 'object') return null;
  const width = src[prefix + 'BorderWidth'];
  if (typeof width !== 'number' || !Number.isFinite(width) || width < 0) return null;
  if (width === 0) return { border: 'none' };
  const color = hintColor(src[prefix + 'BorderColor']);
  return color ? { border: `${width}px solid ${color}` } : null;
}

/** The weights the app names. A number 100–900 passes through as CSS. */
const FONT_WEIGHTS = { regular: 400, normal: 400, medium: 500, semibold: 600, bold: 700 };

/** `<prefix>FontWeight` as CSS, or null. */
function weightStyle(src, prefix) {
  const v = src && typeof src === 'object' ? src[prefix + 'FontWeight'] : undefined;
  if (typeof v === 'number') {
    return Number.isInteger(v) && v >= 100 && v <= 900 ? { fontWeight: v } : null;
  }
  const w = FONT_WEIGHTS[String(v == null ? '' : v).trim().toLowerCase()];
  return w ? { fontWeight: w } : null;
}

/** A gradient stop position: 0–1 on the wire, a percentage in CSS. '' when absent. */
function stopAt(v) {
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > 1) return '';
  return ' ' + Number((v * 100).toFixed(4)) + '%';
}

/**
 * The pill's fill: `linear-gradient(<itemGradientAngle>deg, <start> <loc>%, <end> <loc>%)`.
 * Only one of the two colours authored ⇒ that colour as a solid fill. No angle ⇒ 90deg
 * (left → right, the way both the fund pill and the transfer button run on the phone).
 */
function gradientStyle(src, prefix) {
  if (!src || typeof src !== 'object') return null;
  const start = hintColor(src[prefix + 'GradientStartColor']);
  const end = hintColor(src[prefix + 'GradientEndColor']);
  if (!start && !end) return null;
  if (!start || !end) return { background: start || end };
  const a = src[prefix + 'GradientAngle'];
  const angle = typeof a === 'number' && Number.isFinite(a) ? a : 90;
  return {
    background: `linear-gradient(${angle}deg, ${start}${stopAt(src[prefix + 'GradientStartLocation'])}, `
      + `${end}${stopAt(src[prefix + 'GradientEndLocation'])})`,
  };
}

/** The card frame: `mainFrameColor`, `mainBorder*`, `mainCornerRadius`, `mainPadding*`, `mainMargin*`. */
export function pickerFrameStyle(card) {
  const color = hintColor(card && typeof card === 'object' ? card.mainFrameColor : null);
  return mergeStyles(
    color ? { backgroundColor: color } : null,
    borderStyle(card, 'main'),
    dpStyle(card, 'mainCornerRadius', 'borderRadius'),
    dpStyle(card, 'mainPaddingTop', 'paddingTop'),
    dpStyle(card, 'mainPaddingBottom', 'paddingBottom'),
    dpStyle(card, 'mainPaddingLeft', 'paddingLeft'),
    dpStyle(card, 'mainPaddingRight', 'paddingRight'),
    dpStyle(card, 'mainMarginTop', 'marginTop'),
    dpStyle(card, 'mainMarginBottom', 'marginBottom'),
  );
}

/** The stack of pills: `mainSpacing` is the gap between them. */
export function pickerListStyle(card) {
  return dpStyle(card, 'mainSpacing', 'gap');
}

/**
 * A pill's SHAPE — `itemHeight`, `itemCornerRadius`, `itemPaddingLeft/Right`. Shared by the fund
 * pills and the compare card's submit pill, which keeps its own darker colours.
 * A fixed height drops the stylesheet's vertical padding so the text stays centred in it.
 */
export function pickerItemShapeStyle(card) {
  const height = dpStyle(card, 'itemHeight', 'height');
  return mergeStyles(
    height ? Object.assign({ paddingTop: 0, paddingBottom: 0 }, height) : null,
    dpStyle(card, 'itemCornerRadius', 'borderRadius'),
    dpStyle(card, 'itemPaddingLeft', 'paddingLeft'),
    dpStyle(card, 'itemPaddingRight', 'paddingRight'),
  );
}

/** A fund pill in full: its shape plus text (`itemColor` / `FontSize` / `FontWeight` / `TextAlign`), fill and border. */
export function pickerItemStyle(card) {
  return mergeStyles(
    pickerItemShapeStyle(card),
    textStyle(card, 'item'),
    weightStyle(card, 'item'),
    gradientStyle(card, 'item'),
    borderStyle(card, 'item'),
  );
}

/**
 * The ► on a fund pill. The phone draws it WHITE on the config's own fill — on today's green
 * pill and on the transfer button alike — so once the config authors a fill the stylesheet's
 * grey arrow (chosen for its pale fallback pill) is replaced. No fill authored ⇒ null.
 * 🔴 No config key for the arrow is known yet; if mobile reads one, bind it here instead.
 */
export function pickerArrowStyle(card) {
  return gradientStyle(card, 'item') ? { color: '#ffffff' } : null;
}
